import { PhaseChangeEvent } from "../Event/PhaseChangeEvent";
import { ChunkChangeEvent } from "../Event/ChunkChangeEvent";
import { ChunkRangeChangeEvent } from "../Event/ChunkRangeChangeEvent";
import { Matchset } from "../Data/SporteventSet/Matchset";
import { Chunk } from "../Data/Chunk";
import { TimeUpdateEvent, MaintenanceEvent, VideoReloadEvent, ModelUpdateEvent,
         AuthorityCodeChangeEvent, Controller, ChannelSwitchEvent, Phase, Base } from "@gaming/srvg-classes-core";
import { Timeline } from "../Data/Timeline";
import { Competition } from "../Data/Competition";
import { CompetitionStage, COMPETITIONTYPE_CUP, COMPETITIONTYPE_LEAGUE } from "../Data/CompetitionStage";
import { CHUNKRANGE_MODEL_CHUNKS } from "../Model/ChunkRangeModel";
import { Match } from "../Data/Match";
import { ITimeline } from "../types/Timeline";

// uses srvg.timelinePhases, srvg.debug, srvg.debugView

export class TimelineController extends Controller {
    /**
     * URL for the timings JSON API
     */
    timingsUrl: string;
    /**
     * URL for the matches JSON API
     */
    matchesUrl: string;

    /**
     * The timeline data collection
     */
    timeline: Timeline;

    /**
     * Whether this is the first time we receive timings data.
     * If still yes, matches data is requested for the current competition
     */
    firstPoll: boolean;

    /**
     * If true, never change to the next phase.
     * Only set to true for debugging.
     */
    ignorePhaseChange: boolean;

    /**
     * Stores the data that was received with the last timings request
     */
    lastTimelineData: ITimeline;

    /**
     * Whether the last data request contained any new chunk information.
     */
    newChunksInLastData: boolean;

    /**
     * Link to the background process that communicates with the server.
     */
    timelineWorker: Worker;

    /**
     * Url of the instance
     */
    url: string;

    /**
     * constructor
     */
    constructor(options){
        options = options || {};
        options.name = options.name || 'timelineController';
        super(options);

        this.timeline = options.timeline || null;
        this.timingsUrl = options.url || '';
        this.matchesUrl = options.matchesUrl || '';

        this.firstPoll = true;
        this.ignorePhaseChange = false;
        this.newChunksInLastData = false;

        this.generatedEvents.push( PhaseChangeEvent );
        this.generatedEvents.push( ChunkChangeEvent );
        this.generatedEvents.push( TimeUpdateEvent );
        this.generatedEvents.push( MaintenanceEvent );
        this.generatedEvents.push( VideoReloadEvent );
        this.generatedEvents.push( ChunkRangeChangeEvent );
        this.generatedEvents.push( AuthorityCodeChangeEvent );

        this.timeline.currentChunkModel.addSubscriberForEvent(this, ModelUpdateEvent);

        this.url = options.url || '';

        var that = this;

        // Create the background process that polls the server
        this.timelineWorker = new Worker( options.timelineWorkerPath );
        this.timelineWorker.onerror = function(event){
            //throw new Error(event);
            console.warn( event );
        };
        this.timelineWorker.addEventListener('message', function(e){
            switch( e.data.message ) {
                case 'timingsDataReceive':
                    that.lastTimelineData = e.data.data;
                    that.processTimingsData( e.data.data );
                    break;
                case 'matchesDataReceive':
                    that.processMatchesData( e.data.data );
                    break;
            }
        });
    }

    /**
     *
     */
    pollTimings() {
        //console.log( 'Telling worker to poll Timings' );
        this.timelineWorker.postMessage({
            action: 'poll_timings'
        });
    }

    /**
     *
     */
    pollMatches( competition: Competition ) {
        //console.log( 'Telling worker to poll Matches' );
        this.timelineWorker.postMessage({
            action:'poll_matches',
            competition_id: competition.id
        });
    }

    getLocalStoragePrefix() : string {
        return 'srvg_vf'+this.url+'_'+srvg.config.scout;
    }

    /**
     * Called by page view controller on startup
     */
    startPolling(){
        this.sendStoredTimestamps();
        this.timelineWorker.postMessage({
            action: 'set_urls',
            urls: {
                timingsUrl: this.timingsUrl,
                matchesUrl: this.matchesUrl
            }
        });
        this.pollTimings();
    }

    sendStoredTimestamps() {
        try {
            const item = this.getLocalStorageItem();

            this.timelineWorker.postMessage({
                action: 'set_stored_response_timestamps',
                client_timestamp: item.client_timestamp,
                server_timestamp: item.server_timestamp
            });
        } catch(error) {
            //ignore
        }
    }

    getLocalStorageItem() {
        let item: any  = window.localStorage.getItem(this.getLocalStoragePrefix());
        if (item) {
            item = JSON.parse(item);
        }
        return item;
    }

    /**
     * Interprets the data received by poll()
     */
    private processTimingsData( data: ITimeline, channel?:number) {
        try {
            const item = this.getLocalStorageItem();

            // Store the client and server time of the timings response, but only if this is a new response.
            // The web worker can use this to find out whether it gets a cached timings response
            if( !item || (data.server_datetime != item.server_timestamp) ) {
                let client_timestamp = Math.floor(Date.now()/1000);
                let server_timestamp = data.server_datetime;
                window.localStorage.setItem(this.getLocalStoragePrefix(), JSON.stringify({client_timestamp, server_timestamp}));
                this.sendStoredTimestamps();
            }
        } catch(error) {
            console.error('Timestamp could not be saved to localstorage:', error);
        }

        // trigger events only if request came from timeline itself, not from manual call (switch channel etc)
        channel = channel || this.timeline.currentChannel;

        //console.log("channel", channel, data);

        var that = this;
        if (typeof data.maintenance !== "undefined" && data.maintenance === true) {
            this.triggerEvent(new MaintenanceEvent({maintenance: true}));
            return;
        }

        if (data.authority_code){
            this.triggerEvent(new AuthorityCodeChangeEvent({authorityCode: data.authority_code}));
        }else{
            this.triggerEvent(new AuthorityCodeChangeEvent({authorityCode: null}));
        }

        // pause modus
        if (data.channels && data.channels[0] && data.channels[0].phase_id && srvg.timelinePhases["pause"] && srvg.timelinePhases["pause"].id == data.channels[0].phase_id){
            this.triggerEvent(new MaintenanceEvent({pause: true}));
            return;
        }

        // Sometimes, the scout is a little late in updating the timings table.
        // This value is the accepted time variance.
        var tolerance = 5; // seconds

        // Sanity checks
        if( ! data.channels || ! data.competition || ! data.server_datetime || ! data.channels[channel]
            || ! data.channels[channel].active_phase_end_datetime
            || data.server_datetime >= data.channels[channel].active_phase_end_datetime+tolerance
        ) {
            console.error( 'Error: Something is wrong with the timings data', data );
            if( ! data.channels ) { console.error( 'Timing Error: No channel data' ); }
            if( ! data.competition ) { console.error( 'Timing Error: No competition data' ); }
            if( ! data.server_datetime ) { console.error( 'Timing Error: No server time' ); }
            if( data.channels && ! data.channels[channel] ) { console.error( 'Timing Error: No channel data for current channel' ); }
            if( data.channels && data.channels[channel] && ! data.channels[channel].active_phase_end_datetime ) { console.error( 'Timing Error: No active phase end time for current channel' ); }
            if( data.channels && data.channels[channel] && data.channels[channel].active_phase_end_datetime
                && data.server_datetime >= data.channels[channel].active_phase_end_datetime+tolerance
            ) {
                console.error( 'Timing Error: server time "' + (new Date( data.server_datetime*1000 )).toString() + '" >= end of active phase "' + (new Date(data.channels[channel].active_phase_end_datetime*1000)).toString() + '"' );
                that.timelineWorker.postMessage( {
                    action: 'poll_again_error_time_mismatch'
                } );
            }
            else {
                that.timelineWorker.postMessage( {
                    action: 'poll_again_error_no_channel_data'
                } );
            }
            return;
        }

        if (srvg.debugView){
            srvg.debugView.log( this.name, 'Competition ' + data.competition.id ); //+ ' match day ' + data.match.matchset_nr  +': entered new phase ' + this.getGeneralPhaseName(data.phase_id) + ' (ends in ' + (data.active_phase_end_datetime-app.server.getTime()) + ' seconds)' );
        }

        // Tell everyone we can update the current server time
        this.triggerEvent(new TimeUpdateEvent(data.server_datetime));

        // New competition? -> Destroy current one.
        if( this.timeline.currentCompetitionModel.competition && this.timeline.currentCompetitionModel.competition.id != data.competition.id ) {
            this.timeline.currentCompetitionModel.competition.destroy();
            this.timeline.competitionCache.delete( { id: this.timeline.currentCompetitionModel.competition.id } );
            this.timeline.currentCompetitionModel.setCompetition( null );
        }
        // Competition available? Create if not existing and put into cache
        if( data.competition ) {
            this.timeline.competitionCache.getOrSet(
                { id: data.competition.id },
                function() {
                    return new Competition(
                        data.competition.id,
                        data.competition.continuous_counter,
                        data.competition.start_datetime,
                        data.competition.end_datetime,
                        data.competition.competition_type.name,
                        data.competition.competition_type.group_count,
                        data.competition.program_code,
                        data.competition.competition_type_id
                    );
                }
            );
        }
        // No current competition? -> fetch competition we just created and set as current competition
        if( !this.timeline.currentCompetitionModel.competition || this.timeline.currentCompetitionModel.competition.id != data.competition.id ) {
            this.timeline.currentCompetitionModel.setCompetition(
                this.timeline.competitionCache.get( { id: data.competition.id } )
            );
        }
        // Next competition available? Create if not existing and put into cache
        if( data.next_competition ) {
            this.timeline.competitionCache.getOrSet(
                { id: data.next_competition.id },
                function() {
                    return new Competition(
                        data.next_competition.id,
                        data.next_competition.continuous_counter,
                        data.next_competition.start_datetime,
                        data.next_competition.end_datetime,
                        data.next_competition.competition_type.name,
                        data.next_competition.competition_type.group_count,
                        data.next_competition.program_code,
                        data.next_competition.competition_type_id
                    );
                }
            );
        }

        var competitionStageData = data.channels[0].matches[0].matchset.competitionstage;
        var competitionStageType = competitionStageData.competitionstage_type.competitionstage_name.toLowerCase();
        this.timeline.currentCompetitionStageModel.setCompetitionStage( this.timeline.competitionStageCache.getOrSet(
            { id: competitionStageData.id },
            function() {
                return new CompetitionStage(
                    competitionStageData.id,
                    competitionStageData.stage_nr,
                    competitionStageType,
                    competitionStageType == COMPETITIONTYPE_CUP ? competitionStageData.competition.competition_type.clubs_in_knockoutphase : 0 // TODO only needed in KO phase?
                );
            }
        ) );

        // set chunk range model to quiet (we will send the model update event later)
        var oldState = this.timeline.chunkRangeModel.setTriggerEvents( false );
        // update chunk range model according to fetched data
        var firstChunk = this.updateChunksRangeModel( data );
        // trigger phase change event; must be triggered before the chunk range model update event is sent
        this.triggerPhaseChange( data, firstChunk );
        // return to old setting
        this.timeline.chunkRangeModel.setTriggerEvents( oldState );
        // trigger event for delayed model change
        this.timeline.chunkRangeModel.setChunks( this.timeline.chunkRangeModel.getChunks() );

        //this.triggerEvent( new VideoReloadEvent( data.video_reload, !!this.init ) ); FIXME

        if( this.firstPoll ) {
            // this is executed only once, directly after polling the first timeline data, because
            // we need to fetch past match data for the first competition we display
            this.pollMatches( this.timeline.currentCompetitionModel.competition );
            this.firstPoll = false;
        }
    }

    /**
     * Interprets the data received by pollMatches()
     */
    private processMatchesData( data ) {
        // silently set chunk range to empty
        var oldSetting = this.timeline.chunkRangeModel.setTriggerEvents( false );

        var oldChunks = this.timeline.chunkRangeModel.getChunks();

        this.timeline.chunkRangeModel.setChunks([]);
        this.timeline.chunkRangeModel.setTriggerEvents(oldSetting);
        // refill chunk range with season matches.
        this.updateChunksRangeModel(data);

        // add old chunks to chunk set, because of resetting it before, it looses their chunks on season change (VFC-2097)
        for( var i = 0; i < oldChunks.length; i++ ) {
            var chunk = oldChunks[i];
            if( this.timeline.chunkRangeModel.findChunkIndex( chunk ) === null ) {
                this.timeline.chunkRangeModel.addChunk( chunk );
            }
        }

        // trigger update:
        this.timeline.chunkRangeModel.setChunks( this.timeline.chunkRangeModel.getChunks() );
    }

    /**
     * Updates the chunk range model by using the JSON data.
     * Utilizes the caches.
     * Called by processTimingsData() and processMatchesData()
     */
    private updateChunksRangeModel( data: ITimeline ):Chunk {
        var firstChunk:Chunk = null;
        var that = this;
        this.newChunksInLastData = false;

        var competitions = [];

        // create new chunks
        var numberOfChunks = data.channels[0].matches.length;
        for( var i = 0; i < numberOfChunks; i++ ) {
            var chunkMatches:Match[] = [];
            var competitionStageType, competitionStageTypeId;
            for( var channel = 0; channel < data.channels.length; channel++ ) {
                var channelData = data.channels[channel];
                var matchData = channelData.matches[i];
                if( ! matchData ) {
                    console.error( 'Matchdata for channel', channel, ', match number', i, ', is undefined. There are supposed to be', numberOfChunks, 'chunks' );
                    continue;
                }
                var competition = this.timeline.competitionCache.get( { id: matchData.matchset.competitionstage.competition_id } );
                if( ! competition ) {
                    console.error( 'Error: unknown competition #', matchData.matchset.competitionstage.competition_id );
                    break;
                }
                if( $.inArray( competition, competitions ) == -1 ) {
                    competitions.push( competition );
                }
                competitionStageType = matchData.matchset.competitionstage.competitionstage_type.competitionstage_name.toLowerCase();
                competitionStageTypeId = matchData.matchset.competitionstage.competitionstage_type.id;

                var competitionStageData =  matchData.matchset.competitionstage;
                var currentCompetitionStage = this.timeline.competitionStageCache.getOrSet(
                    { id: competitionStageData.id },
                    function() {
                        return new CompetitionStage(
                            competitionStageData.id,
                            competitionStageData.stage_nr,
                            competitionStageType,
                            competitionStageType == COMPETITIONTYPE_CUP ? competitionStageData.competition.competition_type.clubs_in_knockoutphase : 0 // TODO only needed in KO phase?
                        );
                    }
                );

                var matchset = this.timeline.matchsetsCache.getOrSet(
                    { id: matchData.matchset.id },
                    function() {
                        return new Matchset(
                            matchData.matchset.id,
                            matchData.matchset.set_nr,
                            currentCompetitionStage,
                            competition,
                            matchData.matchset.start_datetime,
                            matchData.matchset.end_datetime
                        );
                    }
                );

                var match = this.timeline.matchesCache.getOrSet(
                    { id: matchData.id },
                    function() {
                        return new Match(
                            matchData.id,
                            matchset,
                            matchData.start_datetime,
                            matchData.betstop_datetime,
                            matchData.end_datetime,
                            matchData.vmatch_group.home_club_id,
                            matchData.vmatch_group.away_club_id,
                            matchData.chunk_id
                        );
                    }
                );

                chunkMatches.push( match );
            }
            var chunk = new Chunk(
                data.channels[0].matches[i].matchset_chunk_nr,
                competitionStageType,
                competition,
                chunkMatches,
                competitionStageTypeId
            );
            if( this.timeline.chunkRangeModel.findChunkIndex( chunk ) === null ) {
                this.newChunksInLastData = true;
                this.timeline.chunkRangeModel.addChunk( chunk );
            }
            else {
                // retrieve the instance of this chunk that is stored in the model
                chunk = this.timeline.chunkRangeModel.getChunk( this.timeline.chunkRangeModel.findChunkIndex( chunk ) );
            }
            if( i == 0 ) {
                firstChunk = chunk;
            }
        }
        // console.log( 'competitions', competitions );

        // delete old chunks
        if( this.timeline.chunkRangeModel.length && !this.timeline.chunkRangeModel.getChunk( this.timeline.chunkRangeModel.length-1 ).competition.isEqualTo( this.timeline.chunkRangeModel.getChunk( 0 ).competition ) ) {
            var oldChunks = this.timeline.chunkRangeModel.getChunks();
            var newChunks = [];
            for( var chunkIndex in oldChunks ) {
                var found = false;
                competitions.forEach( function( e ) {
                    if( e.id == oldChunks[chunkIndex].competition.id ) {
                        found = true;
                    }
                } );
                if( found ) {
                    // chunk is in the current or next competition => keep it
                    newChunks.push( oldChunks[chunkIndex] );
                }
            }
            this.timeline.chunkRangeModel.setChunks( newChunks );
        }
        //console.log( 'Chunk range', this.timeline.chunkRangeModel.getChunks() );
        return firstChunk;
    }

    /**
     * Creates and sends the PhaseChangeEvent for the current channel
     */
    private triggerPhaseChange( data: ITimeline, currentChunk ) {
        if( this.ignorePhaseChange ) {
            return;
        }
        var serverDatetime = data.server_datetime;
        // trigger phase change event
        for( var channel = 0; channel < data.channels.length; channel++ ) {

            var channelData = data.channels[channel];

            if( channelData.active_phase_end_datetime && channelData.active_phase_end_datetime > data.server_datetime ) {
                var currentPhase = new Phase( channelData.phase_id, this.timeline.getGeneralPhaseName(channelData.phase_id), channelData.active_phase_start_datetime, channelData.active_phase_end_datetime, channelData.ticker );
                this.timeline.currentPhaseModel.setPhase( currentPhase );
                this.timeline.currentChunkModel.setChunk( currentChunk );
                // FIXME remove PhaseChangeEvent and subscribe to currentPhaseModel's ModelUpdateEvent instead
                // FIXME also, replace PhaseModel, ChunkModel and ChunkRangeModel with a generic PropertyModel class
                if( channel == this.timeline.currentChannel ) {
                    //console.log( 'new phase', currentPhase.name, 'on channel', channel, currentChunk );
                    this.triggerEvent( new PhaseChangeEvent(
                        channel,
                        currentChunk,
                        currentPhase,
                        serverDatetime
                    ) );
                }
            }
        }
    }

    // ************************** Event handlers **************************

    /**
     *
     */
    handleChannelSwitchEvent (event:ChannelSwitchEvent, provider){
        //console.log( this.timeline.currentChannel, event.newChannel );
        if (this.timeline.currentChannel !== event.newChannel){
            //console.log( 'switching channel' );
            var competitionStage = srvg.objectValue(
                this.lastTimelineData,
                'channels.'+event.newChannel+'.matches.0.matchset.competitionstage.competitionstage_type.competitionstage_name'
            );
            this.timeline.currentChannel = event.newChannel;
            // only in cup mode, because in league the timings on all channel are the same
            if (competitionStage !== undefined && competitionStage === "cup"){
                this.processTimingsData(this.lastTimelineData, event.newChannel);
            }
        }
    }

    /**
     * Catch model updates (chunkRangeModel and chunkModel), then repackage and forward them as chunk change events
     */
    handleModelUpdateEvent( event: ModelUpdateEvent, provider ) {
        if( event.model == this.timeline.chunkRangeModel && event.propertyName == CHUNKRANGE_MODEL_CHUNKS ) {
            this.triggerEvent( new ChunkRangeChangeEvent( this.timeline.chunkRangeModel.getChunks() ) );
        }

        // if currentChunkModel is updated
        if(event.model == this.timeline.currentChunkModel ) {
            // send ChunkChangeEvent
            this.triggerEvent(new ChunkChangeEvent(this.timeline.currentChunkModel.chunk));
        }
    }
}
